// Background-music player context.
//
// Owns the single global <audio> element so playback survives page
// navigations. Components consume `useMusicPlayer()` to read state
// (playing / muted / error) and call togglePlay / toggleMute.
//
// Audio source defaults to `/audio/ambient.mp3` (drop your track into
// `frontend/public/audio/ambient.mp3`). Override at build time via
// `REACT_APP_BACKGROUND_MUSIC_URL`.

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";

const DEFAULT_SRC = process.env.REACT_APP_BACKGROUND_MUSIC_URL || "/audio/ambient.mp3";

const MusicPlayerContext = createContext(null);

const STORAGE_KEY = "xaluca.musicPlayer";

const DEFAULT_VOLUME = 0.55;

const clampVolume = (v) => {
  const n = Number(v);
  if (Number.isNaN(n)) return DEFAULT_VOLUME;
  return Math.min(1, Math.max(0, n));
};

const readPersisted = () => {
  if (typeof window === "undefined") return { muted: false, volume: DEFAULT_VOLUME };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      muted: Boolean(parsed.muted),
      volume: parsed.volume == null ? DEFAULT_VOLUME : clampVolume(parsed.volume),
    };
  } catch {
    return { muted: false, volume: DEFAULT_VOLUME };
  }
};

const writePersisted = (state) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* private mode etc. */ }
};

export const MusicPlayerProvider = ({ src = DEFAULT_SRC, children }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(() => readPersisted().muted);
  const [volume, setVolumeState] = useState(() => readPersisted().volume);
  const [error, setError] = useState(false);

  // Initialise audio element once.
  useEffect(() => {
    const persisted = readPersisted();
    const audio = new Audio(src);
    audio.loop = true;
    audio.preload = "auto";
    audio.volume = persisted.volume;
    audio.muted = persisted.muted;
    audioRef.current = audio;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onError = () => setError(true);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("error", onError);
    return () => {
      audio.pause();
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("error", onError);
      audioRef.current = null;
    };
    // src is stable per provider mount; we deliberately ignore isMuted here
    // because the next effect syncs it.
  }, [src]);

  // Sync mute changes to the audio element + localStorage.
  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = isMuted;
    writePersisted({ muted: isMuted, volume });
  }, [isMuted, volume]);

  // Sync volume changes.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (error) return;
    if (audio.paused) {
      const result = audio.play();
      if (result && typeof result.catch === "function") {
        result.catch(() => setError(true));
      }
    } else {
      audio.pause();
    }
  }, [error]);

  const toggleMute = useCallback(() => setIsMuted((m) => !m), []);

  const setVolume = useCallback((v) => {
    const next = clampVolume(v);
    setVolumeState(next);
    // Any explicit volume change above zero implicitly unmutes.
    if (next > 0) setIsMuted(false);
  }, []);

  const value = useMemo(() => ({
    isPlaying, isMuted, volume, error, togglePlay, toggleMute, setVolume,
  }), [isPlaying, isMuted, volume, error, togglePlay, toggleMute, setVolume]);

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  );
};

export const useMusicPlayer = () => {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) {
    // Safe defaults so components don't crash when used outside the provider.
    return {
      isPlaying: false, isMuted: false, volume: DEFAULT_VOLUME, error: true,
      togglePlay: () => {}, toggleMute: () => {}, setVolume: () => {},
    };
  }
  return ctx;
};
